import React from 'react';
import Icon from '../../../components/AppIcon';

const FeatureSection = () => {
  const features = [
    {
      icon: 'Brain',
      title: 'AI-Powered Recommendations',
      description: 'Our intelligent system learns your giving preferences and suggests causes that align with your values and interests.',
      gradient: 'from-coral-500 to-orange-500'
    },
    {
      icon: 'Trophy',
      title: 'Community Challenges',
      description: 'Join giving challenges with friends, colleagues, and the community to amplify your impact and make giving more engaging.',
      gradient: 'from-teal-500 to-cyan-500'
    },
    {
      icon: 'BarChart3',
      title: 'Impact Tracking',
      description: 'See exactly where your donations go and the real-world impact you\'re creating with detailed reports and visualizations.',
      gradient: 'from-orange-500 to-amber-500'
    },
    {
      icon: 'Shield',
      title: 'Verified Nonprofits',
      description: 'Every organization is thoroughly vetted and verified to ensure your contributions reach legitimate causes.',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      icon: 'FileText',
      title: 'Tax Reports',
      description: 'Automatically generated tax-deductible receipts and year-end summaries for easy filing and record keeping.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'Users',
      title: 'Social Giving',
      description: 'Share your giving journey, inspire others, and see how your community is making a difference together.',
      gradient: 'from-green-500 to-teal-500'
    }
  ];

  return (
    <section id="features" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-coral-100 text-black px-4 py-2 rounded-full mb-4 text-sm font-medium">
            <Icon name="Sparkles" size={16} />
            <span>Powerful Features</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Everything You Need to{' '}
            <span className="bg-gradient-to-r from-coral-500 to-orange-500 bg-clip-text text-transparent">
              Give Smarter
            </span>
          </h2>
          <p className="text-xl text-black max-w-3xl mx-auto">
            Share Good combines cutting-edge technology with intuitive design to make charitable giving seamless, impactful, and rewarding.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features?.map((feature, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-white to-orange-50 rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 border border-orange-100 hover:border-coral-300 transform hover:-translate-y-1"
            >
              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${feature?.gradient} rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                <Icon name={feature?.icon} size={28} className="text-white" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-black mb-3 group-hover:text-coral-600 transition-colors">
                {feature?.title}
              </h3>
              <p className="text-black leading-relaxed">
                {feature?.description}
              </p>
            </div>
          ))}
        </div>

        {/* How It Works Section */}
        <div id="how-it-works" className="mt-24">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-black mb-4">
              Getting Started is{' '}
              <span className="bg-gradient-to-r from-coral-500 to-orange-500 bg-clip-text text-transparent">
                Simple
              </span>
            </h3>
            <p className="text-lg text-black">Join thousands of changemakers in just three easy steps</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-coral-500 to-orange-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 shadow-xl">
                1
              </div>
              <h4 className="text-xl font-bold text-black mb-3">Create Your Account</h4>
              <p className="text-black">Sign up in seconds with email or social login. It's completely free to join.</p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 shadow-xl">
                2
              </div>
              <h4 className="text-xl font-bold text-black mb-3">Discover Causes</h4>
              <p className="text-black">Browse thousands of verified nonprofits or let our AI recommend causes matching your values.</p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 shadow-xl">
                3
              </div>
              <h4 className="text-xl font-bold text-black mb-3">Start Making Impact</h4>
              <p className="text-black">Donate, track your impact, join challenges, and inspire others in the community.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSection;